import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocalGatewayStatus } from '@/hooks/use-local-gateway-status';
import { retryLocalGatewayProbes } from '@/lib/localGateway';
import { setDiagnosticOverride, } from '@/lib/localGateway/status';
import { cn } from '@/lib/utils';
import { AlertTriangle, Cloud, HardDrive, Loader2, RefreshCw, ShieldAlert, Wrench, } from 'lucide-react';
import { useTranslation } from 'react-i18next';
/**
 * Where a clinic's viewer actually gets its pixels, said out loud.
 *
 * A clinic with an onsite node reads at LAN speed (measured ~90 ms per image
 * against ~866 ms from the cloud), and every way that path can break — no
 * certificate, a clinic firewall, a dismissed browser permission — used to look
 * identical from the inside: "it feels slow". Nobody, radiologist or support,
 * could tell which path a window was on.
 *
 * This is deliberately per-window. A popout viewer runs its own probe in its own
 * JavaScript realm, so two windows on one desk can legitimately disagree; each
 * one tells the truth about itself.
 *
 * Rendered in the viewer toolbar and the worklist header. It is always present,
 * but stays a bare muted icon in the ordinary cloud case so that the ~99% of
 * clinics with no node pay no attention tax for it.
 */
/**
 * Chrome's Local Network Access setting. Shown as text, not a link: a web page
 * is not allowed to navigate to a `chrome://` URL, and there is no
 * `permissions.request()` for this gate either — the prompt only appears when a
 * local fetch is attempted, which is why the retry below re-issues the probe
 * rather than asking the browser for anything.
 */
const CHROME_LOCAL_NETWORK_SETTING = 'chrome://settings/content/localNetworkAccess';
/**
 * Everything that varies by state, in one place: the icon, the chip tone, the
 * heading and body the popover reads, and the short chip text.
 *
 * `probing` and `cloud` carry no `label` — an icon alone is enough for a state
 * that is either momentary or entirely expected — and `cloud`'s tone makes it
 * deliberately the quietest thing on screen, because the ~99% of clinics with
 * no onsite node should pay no attention tax for this control.
 */
const STATES = {
    local: {
        icon: HardDrive,
        tone: 'text-emerald-400 hover:bg-emerald-500/10',
        titleKey: 'imageSource.title.local',
        bodyKey: 'imageSource.body.local',
        labelKey: 'imageSource.label.local',
    },
    probing: {
        icon: Loader2,
        tone: 'text-muted-foreground/70 hover:bg-muted/50',
        titleKey: 'imageSource.title.probing',
        bodyKey: 'imageSource.body.probing',
    },
    cloud: {
        icon: Cloud,
        tone: 'text-muted-foreground/60 hover:bg-muted/50',
        titleKey: 'imageSource.title.cloud',
        bodyKey: 'imageSource.body.cloud',
    },
    'cloud-fallback': {
        icon: AlertTriangle,
        tone: 'text-amber-400 hover:bg-amber-500/10',
        titleKey: 'imageSource.title.cloudFallback',
        bodyKey: 'imageSource.body.cloudFallback',
        labelKey: 'imageSource.label.cloud',
    },
    'permission-denied': {
        icon: ShieldAlert,
        tone: 'text-amber-400 hover:bg-amber-500/10',
        titleKey: 'imageSource.title.permissionDenied',
        bodyKey: 'imageSource.body.permissionDenied',
        labelKey: 'imageSource.label.blocked',
    },
};
const OVERRIDE_NOTE_KEYS = {
    'force-local': 'imageSource.override.forcedLocal',
    'force-cloud': 'imageSource.override.forcedCloud',
};
const OVERRIDE_OPTIONS = [
    { value: null, labelKey: 'imageSource.override.auto' },
    { value: 'force-local', labelKey: 'imageSource.override.forceLocal' },
    { value: 'force-cloud', labelKey: 'imageSource.override.forceCloud' },
];
export function ImageSourceIndicator({ className }) {
    const { t } = useTranslation();
    const { state, override, origins } = useLocalGatewayStatus();
    const { icon: Icon, tone, titleKey, bodyKey, labelKey } = STATES[state];
    const title = t(titleKey);
    // Under an override the state is what support pinned, not what the probe
    // found, so every way of reading the chip has to say so — the wrench icon is
    // decorative and a screen reader or a hover never sees it.
    const overrideNote = override ? t(OVERRIDE_NOTE_KEYS[override]) : null;
    const spokenTitle = overrideNote ? `${title}. ${overrideNote}` : title;
    const hasOrigins = origins.length > 0;
    // Fire-and-forget: the store flips to `probing` synchronously, which is the
    // feedback. Awaiting would mean a control stuck pending for up to ten minutes
    // whenever the browser is holding the probe behind its permission prompt.
    const handleRetry = () => {
        void retryLocalGatewayProbes();
    };
    return (_jsx(TooltipProvider, { delayDuration: 300, children: _jsxs(Popover, { children: [_jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { type: "button", "data-testid": "image-source-indicator", "data-source-state": state, "aria-label": t('imageSource.ariaLabel', { state: spokenTitle }), className: cn(
                                    // min-h/min-w keep the icon-only cloud state a 24px target and
                                    // roughly the size of its neighbours in the viewer toolbar.
                                    'flex min-h-6 min-w-6 shrink-0 items-center justify-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium', tone, className), children: [_jsx(Icon, { className: cn('h-3.5 w-3.5', state === 'probing' && 'animate-spin') }), labelKey && _jsx("span", { children: t(labelKey) }), override && _jsx(Wrench, { className: "h-3 w-3", "aria-hidden": true })] }) }) }), _jsx(TooltipContent, { className: "max-w-xs text-xs", children: spokenTitle })] }), _jsxs(PopoverContent, { align: "end", className: "w-80 p-0 text-xs", children: [_jsxs("div", { className: "space-y-2 px-3 py-3", children: [_jsx("p", { className: "text-sm font-semibold", children: title }), _jsx("p", { className: "text-muted-foreground leading-relaxed", children: t(bodyKey) }), state === 'cloud-fallback' && (_jsx("p", { className: "text-muted-foreground leading-relaxed", children: t('imageSource.action.cloudFallback') })), state === 'permission-denied' && (_jsxs("div", { className: "space-y-1", children: [_jsx("p", { className: "text-muted-foreground leading-relaxed", children: t('imageSource.action.permissionDenied') }), _jsx("code", { className: "bg-muted/60 text-foreground block overflow-x-auto rounded px-2 py-1 font-mono text-[11px] select-all", children: CHROME_LOCAL_NETWORK_SETTING })] })), overrideNote && _jsx("p", { className: "text-amber-400", children: overrideNote }), hasOrigins && (_jsx("div", { className: "flex items-center gap-2 pt-1", children: _jsxs("button", { type: "button", "data-testid": "image-source-retry", onClick: handleRetry, className: "border-border text-foreground hover:bg-muted/50 flex items-center gap-1.5 rounded border px-2 py-1 font-medium", children: [_jsx(RefreshCw, { className: "h-3 w-3" }), t('imageSource.retry')] }) }))] }), hasOrigins && (_jsxs("div", { className: "border-border border-t px-3 py-2", children: [_jsx("p", { className: "text-muted-foreground/70 mb-1 text-[10px] font-semibold tracking-wide uppercase", children: t('imageSource.serverAddress') }), origins.map((origin) => (_jsx("code", { className: "text-muted-foreground block overflow-x-auto font-mono text-[11px]", children: origin }, origin)))] })), _jsxs("div", { className: "border-border border-t px-3 py-2", children: [_jsx("p", { className: "text-muted-foreground/70 mb-1.5 text-[10px] font-semibold tracking-wide uppercase", children: t('imageSource.diagnosticHeading') }), _jsx("div", { className: "flex flex-wrap gap-1", children: OVERRIDE_OPTIONS.map((option) => (_jsx("button", { type: "button", "data-testid": `image-source-override-${option.value ?? 'auto'}`, "aria-pressed": override === option.value, onClick: () => setDiagnosticOverride(option.value), className: cn('rounded border px-2 py-1 font-medium', override === option.value
                                            ? 'border-primary bg-primary/15 text-foreground'
                                            : 'border-border text-muted-foreground hover:bg-muted/50'), children: t(option.labelKey) }, option.value ?? 'auto'))) }), _jsx("p", { className: "text-muted-foreground/70 mt-1.5 leading-relaxed", children: t('imageSource.diagnosticNote') })] })] })] }) }));
}
